import { t } from '../../i18n';
import React from 'react';

const RICH_NOTICE_TYPE = 'npc_notice_rich';

function Badge({ children }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
      {children}
    </span>
  );
}

function fontWeightValue(value) {
  if (value === 'medium' || value === '500') return 500;
  if (value === 'bold' || value === '600' || value === '700') return 700;
  return value || 'normal';
}

function safeColor(value, fallback = '#ffffff') {
  return /^#[0-9A-Fa-f]{6}$/.test(String(value || '')) ? String(value) : fallback;
}

function renderTextSegments(block, baseStyle) {
  const segments = Array.isArray(block?.segments) ? block.segments : [];
  if (!segments.length) return String(block?.text || '');

  return segments.map((segment, index) => {
    const text = String(segment?.text || '');
    if (!text) return null;
    const style = segment?.style || {};
    const linkUrl = String(segment?.linkUrl ?? segment?.link_url ?? '').trim();
    const segmentStyle = {
      fontSize: `${Number(style.fontSize ?? baseStyle.fontSize ?? 16)}px`,
      fontWeight: fontWeightValue(style.fontWeight ?? baseStyle.fontWeight),
      color: safeColor(style.color, safeColor(baseStyle.color, '#ffffff')),
      fontStyle: String(style.fontStyle ?? baseStyle.fontStyle ?? 'normal') === 'italic' ? 'italic' : 'normal',
      textDecoration: String(style.textDecoration ?? baseStyle.textDecoration ?? 'none').includes('underline') ? 'underline' : 'none',
    };
    const content = <span key={`seg-${index}`} style={segmentStyle}>{text}</span>;
    if (!/^https?:\/\//i.test(linkUrl)) return content;
    return <a key={`seg-link-${index}`} href={linkUrl} target="_blank" rel="noopener noreferrer">{content}</a>;
  });
}

function imageDisplay(block) {
  const raw = block?.display || block?.imageDisplay;
  if (!raw || typeof raw !== 'object') return null;
  const width = Number(raw.widthPercent ?? raw.width_percent);
  const height = Number(raw.heightPx ?? raw.height_px);
  if (!Number.isFinite(width) && !Number.isFinite(height)) return null;
  return {
    widthPercent: Math.max(20, Math.min(100, Math.round(Number.isFinite(width) ? width : 100))),
    heightPx: Math.max(100, Math.min(720, Math.round(Number.isFinite(height) ? height : 280))),
  };
}

function parseRichBlocks(content = '') {
  const raw = String(content || '').trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.type === RICH_NOTICE_TYPE && Array.isArray(parsed.blocks)) return parsed.blocks;
  } catch (_) {}
  return null;
}

function parseLegacyContent(content = '') {
  const raw = String(content || '');
  if (!raw.trim()) return [];
  const regex = /\[img:([^\]]+)\]/g;
  const blocks = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(raw)) !== null) {
    const text = raw.slice(lastIndex, match.index);
    if (text) blocks.push({ type: 'text', text });
    blocks.push({ type: 'image', url: match[1], alt: 'notice-image' });
    lastIndex = regex.lastIndex;
  }
  const tail = raw.slice(lastIndex);
  if (tail) blocks.push({ type: 'text', text: tail });
  return blocks;
}

function renderRichBlock(block, idx) {
  if (!block || typeof block !== 'object') return null;

  if (block.type === 'image' && block.url) {
    const display = imageDisplay(block);
    return (
      <div
        key={block.id || idx}
        className="relative mx-auto my-4 overflow-hidden rounded-lg border border-gray-700 bg-black"
        style={display ? { width: `${display.widthPercent}%`, height: `${display.heightPx}px` } : undefined}
      >
        <img
          src={block.url}
          alt={block.alt || ''}
          className={display ? 'absolute left-1/2 top-1/2 block h-auto max-h-none max-w-none' : 'block h-auto max-w-full'}
          style={display ? { width: `${10000 / display.widthPercent}%`, transform: 'translate(-50%, -50%)' } : undefined}
        />
      </div>
    );
  }

  if (block.type === 'text') {
    const style = block.style || {};
    const textType = style.textType || 'paragraph';
    const blockStyle = {
      fontSize: `${Number(style.fontSize || (textType === 'heading' ? 24 : 16))}px`,
      fontWeight: fontWeightValue(style.fontWeight),
      color: safeColor(style.color, '#ffffff'),
      fontStyle: String(style.fontStyle || 'normal') === 'italic' ? 'italic' : 'normal',
      textDecoration: String(style.textDecoration || 'none').includes('underline') ? 'underline' : 'none',
      textAlign: ['left', 'center', 'right'].includes(String(style.textAlign)) ? style.textAlign : 'left',
      lineHeight: textType === 'heading' ? 1.25 : 1.6,
      opacity: textType === 'small' ? 0.82 : 1,
      borderLeft: textType === 'quote' ? '3px solid rgba(255,255,255,.35)' : undefined,
      paddingLeft: textType === 'quote' ? 10 : undefined,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      margin: '10px 0',
    };
    return <div key={block.id || idx} style={blockStyle}>{renderTextSegments(block, style)}</div>;
  }

  return null;
}

export default function NoticePreview({ notice, onEdit, onDelete }) {
  if (!notice) {
    return <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-10 text-center text-gray-500">{t('왼쪽에서 공지를 선택해줘.')}</div>;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{notice.isPinned ? '📌 ' : ''}{notice.title || t('(제목 없음)')}</div>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <Badge>{notice.language || 'EN'}</Badge>
            <Badge>{notice.isPublished ? t('게시') : t('임시저장')}</Badge>
            <span className="text-xs text-gray-500">updated: {formatDate(notice.updatedAt || notice.createdAt)}</span>
            <span className="text-xs text-gray-500">writer: <span className="text-gray-700">{notice.writer || '-'}</span></span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onEdit} className="px-3 py-2 rounded border border-gray-200 hover:border-gray-400">{t('수정')}</button>
          <button onClick={onDelete} className="px-3 py-2 rounded bg-red-500 text-white hover:bg-red-600">{t('삭제')}</button>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <NoticeContentView content={notice.content} />
      </div>
    </div>
  );
}

function NoticeContentView({ content }) {
  const richBlocks = parseRichBlocks(content);
  if (richBlocks) {
    return <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">{richBlocks.map(renderRichBlock)}</div>;
  }

  const blocks = parseLegacyContent(content);
  if (blocks.length === 0) return <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{t('(본문 없음)')}</div>;

  return (
    <div className="space-y-4">
      {blocks.map((block, idx) => block.type === 'image'
        ? <div key={`${block.type}-${idx}`} className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50"><img src={block.url} alt={block.alt || `notice-${idx}`} className="block max-w-full h-auto" /></div>
        : <div key={`${block.type}-${idx}`} className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{block.text}</div>)}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (_) {
    return '-';
  }
}

function pad(n) {
  return String(n).padStart(2, '0');
}
